// ── Bank Activity ────────────────────────────────────────────────────────────
//
// Six rules over borrower bank feeds, as one worklist (dbo.usp_ScanBankAlerts):
//   Bank feed stale · Negative balance · Cash below 2 months of payments ·
//   Large repeating debit · Negative cash trend · Large deposit, new payer
//
// Acknowledge records the facts as they stand; if they change, the next scan
// reopens it. Cleared is set by the scan when a condition stops being true —
// nobody clears an alert by hand, because "it went away" is a fact about the
// data, not a judgement.
//
// Notes: each alert has its own thread (dbo.Notes, entitytype 'BankAlert',
// source 'Bank Activity'), opened from the Notes column. They carry the
// borrower's accountid, so the same notes show on the account's Notes tab.
// A note typed when acknowledging or reopening is added to the thread too.
//
// accountid: embedded on the Account page's Monitoring tab — one borrower,
// every status, no book-wide tiles, no Run Scan, no Borrower column.
import { useState, useEffect, Fragment } from "react";
import { NotesPanel } from "../components/NotesPanel";
import { B } from "../../../shared/theme";
import { fmtDate, exportCSV } from "../../../shared/format";
import { Btn, Card, ErrorMsg, Field, Modal, SearchBar, SectionHeader, Spinner, Td, STh } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";

const SEV = {
  High:   { bg: "#FBE4DD", fg: "#993C1D" },
  Medium: { bg: "#FDF0D5", fg: "#7A5200" },
  Low:    { bg: "#F1EFE8", fg: "#5F5E5A" },
};
const STATUS_TONE = {
  Open:         { bg: "#E6F7FC", fg: B.cyanDark },
  Acknowledged: { bg: "#FDF0D5", fg: "#7A5200" },
  Cleared:      { bg: "#E1F5EE", fg: "#0F6E56" },
};
const Pill = ({ tone, children, title }) => (
  <span title={title} style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap",
                               background: tone.bg, color: tone.fg }}>{children}</span>
);
const Tile = ({ label, value, tone, onClick, active, sub }) => (
  <div onClick={onClick}
       style={{ background: active ? "#E6F7FC" : "var(--color-background-secondary)",
                border: "0.5px solid " + (active ? B.cyan : "var(--color-border-tertiary)"),
                borderRadius: 8, padding: "10px 14px", cursor: onClick ? "pointer" : "default", minWidth: 140, flex: "1 1 140px" }}>
    <div style={{ fontSize: 11, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: tone || "inherit" }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>{sub}</div>}
  </div>
);
const when = d => d ? new Date(d).toLocaleString() : "—";
const STATUSES = ["Open", "Acknowledged", "Cleared", "all"];

export const BankActivityPage = ({ accountid = null } = {}) => {
  const { api, go, role } = useApp();
  const isAdmin = !!(role && role.isAdmin);   // scans are admin-only; the nightly run covers everyone
  const embedded = !!accountid;
  const TableBox = embedded ? "div" : Card;
  const [status, setStatus] = useState(embedded ? "all" : "Open");
  const [rule, setRule] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [decide, setDecide] = useState(null);
  const [running, setRunning] = useState(false);
  const [runMsg, setRunMsg] = useState(null);
  const [showRules, setShowRules] = useState(false);
  const [openNotes, setOpenNotes] = useState({});

  const load = (s = status, keepSel = false) => {
    setErr(null);
    api.get("getBankAlerts", embedded ? { status: s, accountid } : { status: s })
      .then(d => { setData(d); if (!keepSel) setSel({}); })
      .catch(e => setErr(e.message));
  };
  useEffect(() => { load(status); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const runScan = async () => {
    setRunning(true); setRunMsg(null);
    try {
      const r = await api.post("runBankAlertScan", {});
      setRunMsg(`${r.found || 0} conditions found · ${r.opened || 0} new · ${r.reopened || 0} reopened · ${r.cleared || 0} cleared`);
      load();
    } catch (e) { setRunMsg("Scan failed: " + e.message); }
    setRunning(false);
  };

  const rows = (data && data.alerts || [])
    .filter(a => !rule || a.rulekey === rule)
    .filter(a => !q || [a.companyname, a.title, a.detail, a.rulename, a.lastnote, a.lastnoteby].some(x => String(x || "").toLowerCase().includes(q.toLowerCase())))
    .map(a => ({ ...a, sevrank: a.severity === "High" ? 3 : a.severity === "Medium" ? 2 : 1 }));
  const { sorted, sortKey, sortDir, toggle } = useSortable(rows, "", "desc");
  const H = ({ label, k, numeric }) =>
    <STh label={label} sortKey={k} currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric={numeric} />;

  if (err) return <ErrorMsg msg={err} onRetry={() => load()} />;
  if (!data) return <Spinner />;

  const rules = data.rules || [];
  const openBy = k => (data.counts || []).filter(c => c.rulekey === k && c.status === "Open").reduce((t, c) => t + c.n, 0);
  const selIds = Object.keys(sel).filter(k => sel[k]).map(Number);
  const allSel = sorted.length > 0 && sorted.every(a => sel[a.id]);
  const lr = data.lastRun;
  const cols = embedded ? 8 : 9;

  return <>
    {!embedded && <SectionHeader title="Bank Activity" count={sorted.length} actions={[
      <SearchBar key="q" value={q} onChange={setQ} placeholder="Borrower, alert or detail…" />,
      <Btn key="r" onClick={() => setShowRules(true)}>Rules</Btn>,
      <Btn key="x" onClick={() => exportCSV(sorted, ["companyname", "rulename", "severity", "title", "detail", "status",
          "firstseen", "lastseen", "decidedby", "decidedat", "note", "notecount", "lastnote", "lastnoteby", "lastnoteat"], "bank_activity.csv")}>Export</Btn>,
      isAdmin && <Btn key="run" variant="primary" disabled={running} onClick={runScan}>{running ? "Scanning…" : "Run Scan"}</Btn>,
    ].filter(Boolean)} />}

    {!embedded && <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
      {rules.map(r => {
        const n = openBy(r.rulekey);
        return <Tile key={r.rulekey} label={r.displayname} value={r.isactive ? n : "off"}
                     tone={n ? (r.severity === "High" ? "#993C1D" : "#7A5200") : undefined}
                     active={rule === r.rulekey} onClick={() => setRule(rule === r.rulekey ? null : r.rulekey)}
                     sub={r.isactive ? "open" : "switched off"} />;
      })}
      <Tile label="Last Scan" value={lr ? fmtDate(lr.startedat) : "never"}
            sub={lr ? (lr.errormessage ? "failed: " + lr.errormessage.slice(0, 60)
                       : `${lr.opened || 0} new · ${lr.reopened || 0} reopened · ${lr.cleared || 0} cleared`) : ""}
            tone={lr && lr.errormessage ? "#993C1D" : undefined} />
    </div>}

    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
      {STATUSES.map(s => (
        <span key={s} onClick={() => setStatus(s)}
              style={{ cursor: "pointer", fontSize: 12, padding: "4px 12px", borderRadius: 14,
                       fontWeight: status === s ? 700 : 400,
                       background: status === s ? B.cyan : "transparent", color: status === s ? "#fff" : B.cyanDark,
                       border: "1px solid " + (status === s ? B.cyan : B.cyanLight) }}>{s === "all" ? "All" : s}</span>
      ))}
      {rule && <span onClick={() => setRule(null)} style={{ cursor: "pointer", fontSize: 12, color: B.cyanDark, marginLeft: 6 }}>
        {(rules.find(r => r.rulekey === rule) || {}).displayname} ✕</span>}
      {runMsg && <span style={{ fontSize: 12, color: "var(--color-text-secondary)", marginLeft: 10 }}>{runMsg}</span>}
      {selIds.length > 0 && <span style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{selIds.length} selected</span>
        <Btn onClick={() => setDecide({ ids: selIds, status: "Acknowledged" })}>Acknowledge</Btn>
        <Btn onClick={() => setDecide({ ids: selIds, status: "Open" })}>Reopen</Btn>
      </span>}
    </div>

    {/* Embedded on an account's Monitoring tab the table sits directly in that
        tab's card, like the UCC and tax tables — not in a card of its own. */}
    <TableBox style={embedded ? { overflowX: "auto" } : { padding: 0, overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>
          <th style={{ width: 30, padding: "8px 10px", background: "var(--color-background-secondary)" }}>
            <input type="checkbox" checked={allSel}
                   onChange={e => setSel(e.target.checked ? Object.fromEntries(sorted.map(a => [a.id, true])) : {})} />
          </th>
          {!embedded && <H label="Borrower" k="companyname" />}
          <H label="Alert" k="title" />
          <H label="Severity" k="sevrank" />
          <H label="First seen" k="firstseen" />
          <H label="Last seen" k="lastseen" />
          <H label="Status" k="status" />
          <H label="Notes" k="lastnoteat" />
          <th style={{ background: "var(--color-background-secondary)" }} />
        </tr></thead>
        <tbody>
          {sorted.length === 0 && <tr><td colSpan={cols} style={{ padding: 24, textAlign: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>
            {embedded && status === "all" ? "No bank activity alerts for this borrower."
              : status === "Open" ? "Nothing open." : "No alerts with this status."}
          </td></tr>}
          {sorted.map(a => (
            <Fragment key={a.id}>
            <tr style={{ opacity: a.status === "Cleared" && !openNotes[a.id] ? 0.65 : 1,
                         background: openNotes[a.id] ? "#F4FAFC" : undefined }}>
              <Td style={{ width: 30 }}>
                <input type="checkbox" checked={!!sel[a.id]} onChange={e => setSel(s => ({ ...s, [a.id]: e.target.checked }))} />
              </Td>
              {!embedded && <Td style={{ fontWeight: 500 }}>
                <span style={{ color: B.cyanDark, cursor: "pointer" }} onClick={() => go("account", { accountid: a.accountid, companyname: a.companyname })}>
                  {a.companyname || a.accountid}</span>
              </Td>}
              <Td style={{ maxWidth: 560 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.05em" }}>{a.rulename}</div>
                <div style={{ fontWeight: 500 }}>{a.title}</div>
                <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{a.detail}</div>
                {a.lastnote
                  ? <div style={{ fontSize: 11, marginTop: 3, whiteSpace: "pre-wrap" }}><b>Latest note:</b> {a.lastnote}</div>
                  : a.note && <div style={{ fontSize: 11, marginTop: 3, whiteSpace: "pre-wrap" }}><b>Note:</b> {a.note}</div>}
              </Td>
              <Td><Pill tone={SEV[a.severity] || SEV.Low}>{a.severity}</Pill></Td>
              <Td style={{ whiteSpace: "nowrap", fontSize: 12 }}>{fmtDate(a.firstseen)}</Td>
              <Td style={{ whiteSpace: "nowrap", fontSize: 12 }}>{fmtDate(a.lastseen)}</Td>
              <Td>
                <Pill tone={STATUS_TONE[a.status] || STATUS_TONE.Open}
                      title={a.decidedby ? `${a.status} by ${a.decidedby} · ${when(a.decidedat)}` : a.clearedat ? "Cleared " + when(a.clearedat) : ""}>{a.status}</Pill>
                {a.reopencount > 0 && a.status === "Open" && <div style={{ fontSize: 11, color: "#993C1D", marginTop: 2 }}>reopened</div>}
              </Td>
              <Td style={{ whiteSpace: "nowrap", fontSize: 12 }}>
                <span onClick={() => setOpenNotes(o => ({ ...o, [a.id]: !o[a.id] }))}
                      title={openNotes[a.id] ? "Hide notes" : "Show and add notes"}
                      style={{ cursor: "pointer", color: B.cyanDark, fontWeight: 600 }}>
                  {openNotes[a.id] ? "▾ " : "▸ "}{a.notecount ? `${a.notecount} note${a.notecount > 1 ? "s" : ""}` : "Add note"}
                </span>
                {a.notecount > 0 && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>
                  {String(a.lastnoteby || "").split("@")[0]} · {fmtDate(a.lastnoteat)}</div>}
              </Td>
              <Td style={{ whiteSpace: "nowrap" }}>
                {a.status === "Open"
                  ? <Btn onClick={() => setDecide({ ids: [a.id], status: "Acknowledged" })}>Acknowledge</Btn>
                  : a.status === "Acknowledged" ? <Btn onClick={() => setDecide({ ids: [a.id], status: "Open" })}>Reopen</Btn> : null}
              </Td>
            </tr>
            {openNotes[a.id] && <tr style={{ background: "#F4FAFC" }}>
              <td colSpan={cols} style={{ padding: "4px 16px 12px 46px", borderBottom: "0.5px solid var(--color-border-tertiary)" }}>
                <NotesPanel compact entitytype="BankAlert" entityid={String(a.id)} accountid={a.accountid}
                            source="Bank Activity" placeholder="What you did, who you spoke to, what happens next…"
                            onChange={() => load(status, true)} />
              </td>
            </tr>}
            </Fragment>
          ))}
        </tbody>
      </table>
    </TableBox>
    {!embedded && <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 8 }}>
      Funded borrowers (Accrual / Non Accrual), bank data on or after first funding. Runs nightly with the monitoring sweep.
      Cleared alerts are ones the scan found are no longer true.
    </div>}

    {decide && <DecisionModal decide={decide} onClose={() => setDecide(null)} onSaved={() => { setDecide(null); load(); }} />}
    {showRules && <RulesModal rules={rules} onClose={() => setShowRules(false)}
                              onChanged={() => load()} onRunScan={runScan} running={running} runMsg={runMsg} />}
  </>;
};

// ── Rules window ──
// Everyone with the page sees the rules. Administrators can switch a rule on
// or off and change its thresholds here (api/saveBankAlertRule); a threshold
// can only be edited where the rule already has one. Nothing rescans on save —
// press Run Scan when the changes are done.
const RulesModal = ({ rules, onClose, onChanged, onRunScan, running, runMsg }) => {
  const { api, role } = useApp();
  const isAdmin = !!(role && role.isAdmin);
  const [edits, setEdits] = useState({});      // { rulekey: { p1, p2, p3 } } as typed
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(false);   // something changed since the last scan

  const val = (r, p) => (edits[r.rulekey] && edits[r.rulekey][p] !== undefined) ? edits[r.rulekey][p] : String(Number(r[p]));
  const dirty = (r) => ["p1", "p2", "p3"].some(p => r[p] != null && edits[r.rulekey] && edits[r.rulekey][p] !== undefined
                                                   && edits[r.rulekey][p] !== String(Number(r[p])));
  const save = async (r, body) => {
    setBusy(r.rulekey); setErr(null);
    try {
      await api.post("saveBankAlertRule", { rulekey: r.rulekey, ...body });
      setEdits(e => { const n = { ...e }; delete n[r.rulekey]; return n; });
      setSaved(true);
      onChanged();
    } catch (e) { setErr(e.message); }
    setBusy(null);
  };
  const saveThresholds = (r) => {
    const body = {};
    ["p1", "p2", "p3"].forEach(p => { if (r[p] != null) body[p] = val(r, p); });
    save(r, body);
  };
  const inp = { width: 70, fontSize: 12, padding: "3px 6px", border: "1px solid #c8cdd2", borderRadius: 4, textAlign: "right" };

  return <Modal title="Bank activity rules" onClose={onClose} wide>
    <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 12, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
      <span>{isAdmin
        ? "Switch a rule on or off, or change its thresholds. Nothing rescans on save — press Run Scan when you are done."
        : "Only an administrator can change these."}</span>
      {isAdmin && <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
        {saved && !running && <span style={{ fontSize: 12, color: "#7A5200" }}>Changed — run the scan to apply</span>}
        {runMsg && <span style={{ fontSize: 12 }}>{runMsg}</span>}
        <Btn variant="primary" disabled={running} onClick={() => { setSaved(false); onRunScan(); }}>{running ? "Scanning…" : "Run Scan"}</Btn>
      </span>}
    </div>
    {err && <div style={{ color: "#993C1D", fontSize: 13, marginBottom: 10 }}>{err}</div>}
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead><tr>{["Rule", "Severity", "What it checks", "Thresholds", "On"].map(h =>
        <th key={h} style={{ textAlign: "left", fontSize: 11, color: B.cyanDark, padding: "6px 8px", borderBottom: "1.5px solid " + B.cyanLight }}>{h}</th>)}</tr></thead>
      <tbody>{rules.map(r => (
        <tr key={r.rulekey} style={{ opacity: r.isactive ? 1 : 0.6 }}>
          <Td style={{ fontWeight: 600 }}>{r.displayname}<div style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--color-text-secondary)" }}>{r.rulekey}</div></Td>
          <Td><Pill tone={SEV[r.severity] || SEV.Low}>{r.severity}</Pill></Td>
          <Td style={{ fontSize: 12 }}>{r.description}</Td>
          <Td style={{ fontSize: 12 }}>
            {isAdmin
              ? <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {["p1", "p2", "p3"].filter(p => r[p] != null).map(p => (
                    <label key={p} style={{ display: "flex", gap: 4, alignItems: "center" }}>{p}
                      <input value={val(r, p)} style={inp} disabled={busy === r.rulekey}
                             onChange={e => setEdits(x => ({ ...x, [r.rulekey]: { ...(x[r.rulekey] || {}), [p]: e.target.value } }))} />
                    </label>))}
                  {dirty(r) && <Btn variant="primary" disabled={busy === r.rulekey} onClick={() => saveThresholds(r)}>{busy === r.rulekey ? "…" : "Save"}</Btn>}
                </div>
              : [r.p1, r.p2, r.p3].map((v, i) => v == null ? null : `p${i + 1} = ${Number(v)}`).filter(Boolean).join(" · ")}
            <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 3 }}>{r.paramnote}</div>
          </Td>
          <Td>
            {isAdmin
              ? <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer", whiteSpace: "nowrap" }}>
                  <input type="checkbox" checked={!!r.isactive} disabled={busy === r.rulekey}
                         onChange={e => save(r, { isactive: e.target.checked })} />
                  {r.isactive ? "On" : "Off"}
                </label>
              : (r.isactive ? "Yes" : "No")}
          </Td>
        </tr>))}</tbody>
    </table>
  </Modal>;
};

const DecisionModal = ({ decide, onClose, onSaved }) => {
  const { api } = useApp();
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const r = await api.post("saveBankAlert", { ids: decide.ids, status: decide.status, note });
      if (r && r.errors && r.errors.length) setErr(r.errors.map(e => e.error).join("; ")); else onSaved();
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };
  return (
    <Modal title={`${decide.status === "Open" ? "Reopen" : "Acknowledge"} · ${decide.ids.length} alert${decide.ids.length > 1 ? "s" : ""}`} onClose={onClose}>
      <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 14 }}>
        {decide.status === "Open" ? "Put it back on the open list."
          : "You have looked at it. It stays acknowledged while the facts are the same; if they change — a new overdraft day, a new losing streak, a feed that recovers and stops again — it comes back on the next scan."}
      </div>
      <Field label="Note">
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={4} placeholder="What you found"
                  style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", fontSize: 13, borderRadius: 4, border: "1px solid #999", fontFamily: "inherit" }} />
      </Field>
      {err && <div style={{ color: "#993C1D", fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</Btn>
      </div>
    </Modal>
  );
};
